import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  Box,
} from '@mui/material';
import FileUploader from '../components/FileUploader';
import api from '../axios/api';

import { useDispatch } from 'react-redux';
import { showNotification } from '../redux/slices/notificationSlice';
import CustomButton from '../componentsII/Buttons';
import Loader from "../componentsII/Loader";
import { useSelector } from 'react-redux';

// Validation schema
const validationSchema = yup.object().shape({
  companyFullName: yup.string().required('Company name is required'),
  sector: yup.string().required('Sector is required'),
   otherSector: yup.string().when('sector', {
    is: (sector) => sector === 0 || sector === '0', // Check for number 0 or string '0'
    then: () => yup.string().required('Other sector is required'),
    otherwise: () => yup.string()
  }),
  industry: yup.string().required('Industry is required'),
  otherInd: yup.string().when('industry', {
    is: (industry) => industry === 0 || industry === '0', // Check for number 0 or string '0'
    then: () => yup.string().required('Other industry is required'),
    otherwise: () => yup.string()
  }),
  subindustry: yup.string().required('Sub-industry is required'),
  otherSubind: yup.string().when('subindustry', {
    is: (subindustry) => subindustry === 0 || subindustry === '0', // Check for number 0 or string '0'
    then: () => yup.string().required('Other sub-industry is required'),
    otherwise: () => yup.string()
  }),
  // revenue: yup.string().required('Revenue is required'),
  revenue: yup
    .string()
    .required('Revenue is required')
    .matches(
      /^\d{1,13}(\.\d{1,2})?$/,
      'Revenue must have at most 13 digits before decimal and 2 digits after'
    )
    .test('max-decimal-digits', 'Revenue must have at most 13 digits before decimal and 2 digits after',
      (value) => {
        if (!value) return true;
        const parts = value.split('.');
        return parts[0].length <= 13 && (!parts[1] || parts[1].length <= 2);
      }
    ),
  revenueCurrency: yup.string().required('Currency is required'),
  headQuarterCountry: yup.string().required('Headquarters country is required'),
  companyWeburl: yup.string().url('Must be a valid URL'),
  companyShortDesc: yup.string()
    .required('Company description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters'),
  file: yup.mixed().test('fileRequired', 'Company logo is required', function (value) {
    const id = this.parent.id;
    if (id === 0) {
      return !!value;
    }
    return true;
  }),
});

const CompanyDetails = () => {
  const [sectors, setSectors] = useState([]);
  const [allIndustries, setAllIndustries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [allSubIndustries, setAllSubIndustries] = useState([]);
  const [subIndustries, setSubIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [companyLogo, setCompanyLogo] = useState('');
  const [dataId, setDataId] = useState(0);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      id: dataId || 0,
      userId: user.id || '',
      companyFullName: '',
      sector: '',
      otherSector: '',
      industry: '',
      otherInd: '',
      subindustry: '',
      otherSubind: '',
      revenue: '',
      revenueCurrency: '',
      headQuarterCountry: '',
      companyWeburl: '',
      companyShortDesc: '',
      file: undefined,
    }
  });

  const watchSector = watch('sector');
  const watchIndustry = watch('industry');
  const watchSubindustry = watch('subindustry');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [sectorRes, countryRes, currencyRes] = await Promise.all([
          api.get("/common/sector"),
          api.get("/common/countryDrop"),
          api.get("/common/countryDrop")
        ]);
        setIsLoading(false);
        setCurrencies(currencyRes.data.data.map(sector => ({
          value: sector.value,
          label: sector.country_currency_code
        })));
        setSectors([
          ...sectorRes.data.data,
          { value: 0, label: "Other" }
        ]);

        setCountries([
          ...countryRes.data.data,
          { value: 0, label: "Other" }
        ]);
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

 useEffect(() => {
  const fetchIndustries = async () => {
    if (watchSector !== null && watchSector !== undefined && watchSector !== '') {
      try {
        setIsLoading(true);
        const response = await api.get(`/common/industry?sector_id=${watchSector}`);
        setIsLoading(false);

        const industriesData = [
          ...response.data.data,
          { value: 0, label: "Other" }
        ];

        setAllIndustries(industriesData);
        setIndustries(industriesData);

        // Reset industry and subindustry selections when sector changes
        // Only reset if this is a user-initiated change, not during initial load
        const currentIndustry = watchIndustry;
        if (currentIndustry && !dataId) {
          setValue('industry', '');
          setValue('subindustry', '');
          setSubIndustries([]);
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching industries:', error);
      }
    } else if (watchSector === '') {
      setIndustries([]);
      setValue('industry', '');
      setValue('subindustry', '');
      setSubIndustries([]);
    }
  };

  fetchIndustries();
}, [watchSector]);

  useEffect(() => {
  const fetchSubIndustries = async () => {
    if (watchIndustry !== null && watchIndustry !== undefined && watchIndustry !== '') {
      try {
        setIsLoading(true);
        const response = await api.get(`/common/subIndustry?industry_id=${watchIndustry}`);
        setIsLoading(false);

        const subIndustriesData = [
          ...response.data.data,
          { value: 0, label: "Other", sub_industry_name: "Other" }
        ];

        setAllSubIndustries(subIndustriesData);
        setSubIndustries(subIndustriesData);

        // Only reset if this is a user-initiated change, not during initial load
        const currentSubindustry = watchSubindustry;
        if (currentSubindustry && !dataId) {
          setValue('subindustry', '');
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching subindustries:', error);
      }
    } else if (watchIndustry === '') {
      setSubIndustries([]);
      setValue('subindustry', '');
    }
  };

  fetchSubIndustries();
}, [watchIndustry]);

useEffect(() => {
  const getUserDetails = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/company/company?user_id=${user.id}`);
      if (response.data.data && response.data.data[0]) {
        const companyData = response.data.data[0];

        // Set form values
        setDataId(companyData.id);
        setValue('id', companyData.id);
        setValue('companyFullName', companyData.company_full_name);
        setValue('sector', companyData.sector);
        setValue('otherSector', companyData.other_sector || '');

        // Load industry options based on sector (including when sector is 0)
        if (companyData.sector !== null && companyData.sector !== undefined) {
          try {
            const industryResponse = await api.get(`/common/industry?sector_id=${companyData.sector}`);
            const industriesData = [
              ...industryResponse.data.data,
              { value: 0, label: "Other" }
            ];
            setAllIndustries(industriesData);
            setIndustries(industriesData);

            // Set industry value
            setValue('industry', companyData.industry);
            setValue('otherInd', companyData.other_ind || '');

            // Load subindustry options based on industry (including when industry is 0)
            if (companyData.industry !== null && companyData.industry !== undefined) {
              const subIndustryResponse = await api.get(`/common/subIndustry?industry_id=${companyData.industry}`);
              const subIndustriesData = [
                ...subIndustryResponse.data.data,
                { value: 0, label: "Other", sub_industry_name: "Other" }
              ];
              setAllSubIndustries(subIndustriesData);
              setSubIndustries(subIndustriesData);

              // Set subindustry value
              setValue('subindustry', companyData.subindustry);
              setValue('otherSubind', companyData.other_subind || '');
            }
          } catch (error) {
            console.error('Error loading dependent dropdowns:', error);
          }
        }

        setValue('revenue', companyData.revenue);
        setValue('revenueCurrency', companyData.revenue_currency);
        setValue('headQuarterCountry', companyData.head_quarter_country);
        setValue('companyWeburl', companyData.company_weburl || '');
        setValue('companyShortDesc', companyData.company_short_desc);

        // Set company logo
        setCompanyLogo(companyData.company_logo);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching user details:', error);
    }
  };

  if (user.id) {
    getUserDetails();
  }
}, [user.id, setValue]);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      formData.append("id", dataId.toString());

      Object.keys(data).forEach(key => {
        if (key !== 'id') {
          formData.append(key, data[key]);
        }
      });

      setIsLoading(true);
      const response = await api.post("/company/addCompany", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        }
      });

      if (response.data.status === "200") {
        setIsLoading(false);
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );

        const userData = JSON.parse(localStorage.getItem('user'));

        // Update the company information
        userData.company = {
          id: Number(response.data.data.id),
          name: response.data.data.name,
          industry: response.data.data.industry,
          industry_name: response.data.data.industry_name,
          other_industry: response.data.data.other_industry,
          sector: response.data.data.sector,
          sector_name: response.data.data.sector_name,
          other_sector: response.data.data.other_sector,
          subindustry: response.data.data.subindustry,
          sub_industry_name: response.data.data.sub_industry_name,
          other_subind: response.data.data.other_subind,
          hqLocation: response.data.data.hqLocation
        };

        // Save updated user data back to localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        window.location.reload();
      } else {
        setIsLoading(false);
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Submission error:', error);
      dispatch(
        showNotification({
          message: error.message,
          type: "error",
        })
      );
    }
  };

  const handleReset = () => {
    reset();
    setCompanyLogo('');
  };

  if (isLoading) {
    return (
      <div className="w-full h-[calc(100vh-200px)] flex justify-center items-center">
        <Loader />
      </div>
    );
  } else {
    return (
      <div className="flex justify-center w-full">
        <Card sx={{ width: '1000px' }}>
         <h1 className="m-2 text-2xl font-bold text-green-600 transition duration-300 ease-in-out hover:text-green-800">
           Company Registration
          </h1>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* Company Full Name */}
                <Grid item xs={12}>
                  <Controller
                    name="companyFullName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <span>
                            Company Full Name
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        fullWidth
                        error={!!errors.companyFullName}
                        helperText={errors.companyFullName?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Company Website */}
                <Grid item xs={12}>
                  <Controller
                    name="companyWeburl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Company Website"
                        fullWidth
                        type="url"
                        error={!!errors.companyWeburl}
                        helperText={errors.companyWeburl?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Sector */}
                <Grid item xs={6}>
                  <Controller
                    name="sector"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={sectors}
                        getOptionLabel={(option) => option.label}
                        value={sectors.find(option => option.value === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Sector
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            error={!!errors.sector}
                            helperText={errors.sector?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="otherSector"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Other Sector"
                        fullWidth
                        disabled={watchSector !== 0}
                        error={!!errors.otherSector}
                        helperText={errors.otherSector?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Industry */}
                <Grid item xs={6}>
                  <Controller
                    name="industry"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={industries}
                        getOptionLabel={(option) => option.label}
                        value={industries.find(option => option.value === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.value)}
                        // disabled={!watchSector || watchSector === ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Industry
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            error={!!errors.industry}
                            helperText={errors.industry?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="otherInd"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Other Industry"
                        fullWidth
                        disabled={watchIndustry !== 0}
                        error={!!errors.otherInd}
                        helperText={errors.otherInd?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Sub-Industry */}
                <Grid item xs={6}>
                  <Controller
                    name="subindustry"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={subIndustries}
                        getOptionLabel={(option) => option.sub_industry_name}
                        value={subIndustries.find(option => option.value === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.value)}
                        // disabled={!watchIndustry || watchIndustry === ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Sub-Industry
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            error={!!errors.subindustry}
                            helperText={errors.subindustry?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="otherSubind"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Other Sub-Industry"
                        fullWidth
                        disabled={watchSubindustry !== 0}
                        error={!!errors.otherSubind}
                        helperText={errors.otherSubind?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Revenue and Currency */}
                <Grid item xs={6}>
                  {/* <Controller
                    name="revenue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <span>
                          Revenue
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        fullWidth
                        type="number"
                        error={!!errors.revenue}
                        helperText={errors.revenue?.message}
                        onBlur={(e) => {
                          const formattedValue = parseFloat(e.target.value).toFixed(2);
                          field.onChange(formattedValue);
                        }}
                      />
                    )}
                  /> */}
                  <Controller
                    name="revenue"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <span>
                            Revenue
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        fullWidth
                        type="text" // Changed from number to text for better decimal control
                        inputProps={{
                          maxLength: 15, // 15 digits + decimal point + 2 decimal places
                          pattern: "^\\d{1,13}(\\.\\d{0,2})?$"
                        }}
                        error={!!errors.revenue}
                        helperText={errors.revenue?.message}
                        onChange={(e) => {
                          // Only allow valid decimal numbers
                          const value = e.target.value;
                          const regex = /^\d{0,15}(\.\d{0,2})?$/;

                          if (value === '' || regex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                        onBlur={(e) => {
                          // Format to always have 2 decimal places when focus is lost
                          const value = e.target.value;
                          if (value && !isNaN(parseFloat(value))) {
                            field.onChange(parseFloat(value).toFixed(2));
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="revenueCurrency"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={currencies}
                        getOptionLabel={(option) => option.label}
                        value={currencies.find(option => option.value === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Currency
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            error={!!errors.revenueCurrency}
                            helperText={errors.revenueCurrency?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                {/* Headquarters Country */}
                <Grid item xs={12}>
                  <Controller
                    name="headQuarterCountry"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.label}
                        value={countries.find(option => option.value === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Headquarters Country
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            error={!!errors.headQuarterCountry}
                            helperText={errors.headQuarterCountry?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                {/* Company Description */}
                <Grid item xs={12}>
                  <Controller
                    name="companyShortDesc"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <span>
                            Company Description
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.companyShortDesc}
                        helperText={errors.companyShortDesc?.message}
                      />
                    )}
                  />
                </Grid>

                {companyLogo && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Current Company Logo
                      </Typography>
                      <img
                        src={companyLogo}
                        alt="Company Logo"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Controller
                    name="file"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <div>
                        <FileUploader
                          multiple={false}
                          name="companylogo"
                          accept="image/*"
                          label={
                            <span>
                              Company Logo
                              {dataId === 0 && <span style={{ color: 'red' }}> *</span>}
                            </span>
                          }
                          onChange={(file) => field.onChange(file?.[0])}
                          error={!!error}
                        />
                        {error && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {error.message}
                          </Typography>
                        )}
                      </div>
                    )}
                  />
                </Grid>

                {/* Buttons */}
                <div className="w-full flex justify-end gap-5 items-center py-5">
                  <CustomButton
                    className="bg-gray-500 hover:bg-gray-700 text-white "
                    click={() => handleReset()}
                    text="Reset"
                  />
                  <CustomButton
                    className="bg-green-600 hover:bg-green-700 text-white "
                    type="submit"
                    text="Submit"
                  />
                </div>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
};

export default CompanyDetails;